import { useApiMutation, useApiQuery } from "@gladis/api-client";
import type { PendingInviteDto, PendingInviteListDto, ResendInviteDto } from "@gladis/shared";
import { useState, type ReactNode } from "react";
import { Button } from "@gladis/ui";
import { tRich, formatDate, errorSentence, useT } from "@gladis/ui/i18n";

/**
 * Invites sent and not yet answered (FR-028 to FR-030).
 *
 * EXPIRED ONES ARE SHOWN, marked, with Resend still offered. The product's own answer to an expired
 * link is "ask the inviter to resend it", which nobody can do if the invite has quietly vanished
 * from the list — and "it disappeared" is a worse thing to explain than "it expired".
 */
export function PendingInvitesList({ householdId }: { householdId: string }): ReactNode {
  const t = useT();
  const invites = useApiQuery<PendingInviteListDto>(
    ["invites", householdId],
    `/households/${householdId}/invites`,
  );

  const rows = invites.data?.invites ?? [];

  if (invites.isPending || rows.length === 0) {
    // Nothing pending is the normal state, and an empty panel saying so is noise on a page that
    // already has plenty. The invite form above it is the affordance.
    return null;
  }

  return (
    <section className="mt-10 rounded-control border border-line bg-panel p-5">
      <h2 className="text-xl font-semibold">{t("identity.invites.heading")}</h2>
      <p className="mt-1 text-sm text-muted">{t("identity.invites.hint")}</p>

      <ul className="mt-4 divide-y divide-line">
        {rows.map((invite) => (
          <PendingInviteRow key={invite.id} householdId={householdId} invite={invite} />
        ))}
      </ul>
    </section>
  );
}

function PendingInviteRow({
  householdId,
  invite,
}: {
  householdId: string;
  invite: PendingInviteDto;
}): ReactNode {
  const t = useT();
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);

  const resend = useApiMutation<ResendInviteDto, void>(
    (client) => client.post(`/invites/${invite.id}/resend`),
    {
      invalidates: [["invites", householdId]],
      onSuccess: (result) => {
        // Shown rather than merely sent, for the same reason invite creation shows it: the copy-link
        // path is the primary route, not a fallback for when mail breaks (AC-H15).
        setLink(result.inviteUrl);
        setCopied(false);
      },
    },
  );

  const revoke = useApiMutation<void, void>(
    (client) => client.post(`/invites/${invite.id}/revoke`),
    { invalidates: [["invites", householdId]] },
  );

  async function copy(url: string): Promise<void> {
    // try/catch is permitted in a domain package's ui/ subpath — this is presentation, and a
    // refused clipboard permission is exactly what it is for.
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // The link is on screen and selectable regardless, so there is nothing to recover from —
      // only a confirmation not to show.
      setCopied(false);
    }
  }

  return (
    <li className="py-3">
      <div className="flex items-baseline justify-between gap-4">
        <div className="min-w-0">
          <p className="truncate">{invite.email}</p>
          <p className="truncate text-sm text-muted">
            {/*
              `?? t(...)` AND NOT A BARE RENDER. `invitedByDisplayName` became nullable in 021, and
              JSX renders null as nothing — so without this the line reads "invited by" followed by
              a space and no one, with nothing failing anywhere.
            */}
            invited by {invite.invitedByDisplayName ?? t("person.formerMember")}
            {invite.isExpired ? (
              <span className="ml-2">· expired {formatDate(invite.expiresAt)}</span>
            ) : (
              <span className="ml-2">· expires {formatDate(invite.expiresAt)}</span>
            )}
          </p>
        </div>

        <div className="flex shrink-0 gap-2">
          <Button type="button" onClick={() => resend.mutate()} disabled={resend.isPending}>
            {resend.isPending ? t("common.sending") : t("identity.invites.resend")}
          </Button>
          <Button type="button" onClick={() => setConfirmingRevoke(true)}>
            {t("identity.invites.revoke")}
          </Button>
        </div>
      </div>

      {link ? (
        <div className="mt-3 rounded-control border border-line p-3">
          {resend.data?.emailDelivery === "failed" ? (
            <p className="text-sm text-muted" role="status">
              {t("identity.invites.mailFailed")}
            </p>
          ) : (
            <p className="text-sm text-muted" role="status">
              {t("identity.invites.resent")}
            </p>
          )}
          <p className="mt-2 break-all font-mono text-sm">{link}</p>
          <Button className="mt-2" type="button" onClick={() => void copy(link)}>
            {copied ? t("common.copied") : t("common.copyLink")}
          </Button>
        </div>
      ) : null}

      {confirmingRevoke ? (
        <div className="mt-3 rounded-control border border-line p-3" role="alert">
          <p>
            {tRich("identity.invites.withdrawTo", {
              email: <strong>{invite.email}</strong>,
            })}
          </p>
          <p className="mt-1 text-sm text-muted">{t("identity.invites.withdrawConsequence")}</p>
          <div className="mt-3 flex gap-3">
            <Button
              variant="primary"
              type="button"
              onClick={() => revoke.mutate()}
              disabled={revoke.isPending}
            >
              {revoke.isPending
                ? t("identity.invites.withdrawing")
                : t("identity.invites.withdraw")}
            </Button>
            <Button type="button" onClick={() => setConfirmingRevoke(false)}>
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      ) : null}

      {resend.error || revoke.error ? (
        <p className="mt-2 text-sm text-muted" role="alert">
          {errorSentence(resend.error ?? revoke.error)}
        </p>
      ) : null}
    </li>
  );
}
